import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, CheckCircle2 } from "lucide-react";
import { usePaywall } from "@/contexts/PaywallContext";
import SubscribeButton from "@/components/SubscribeButton";
import api from "@/lib/api";
import { dateLocale } from "@/lib/utils";
import { useLang } from "@/i18n";

const fmt = (cents) =>
  new Intl.NumberFormat(dateLocale(), { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format((cents || 0) / 100);

const interval = () => {
  try { return localStorage.getItem("athlynx_billing_interval") === "annual" ? "annual" : "monthly"; } catch { return "monthly"; }
};

export default function PaywallModal() {
  const { open, reason, hide } = usePaywall();
  const { tf } = useLang();
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!open || plan) return;
    api.get("/billing/plans")
      .then((r) => setPlan((r.data?.plans || []).find((p) => p.id === "pro") || null))
      .catch(() => {});
  }, [open, plan]);

  const iv = interval();
  const price = plan?.prices?.[iv];
  const features = (plan?.features || []).slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && hide()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-border bg-surface max-h-[90vh] overflow-y-auto" data-testid="paywall-modal">
        <div className="relative">
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-br from-athlynx/20 via-athlynx/5 to-transparent pointer-events-none" />
          <div className="relative p-6 pb-3">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-athlynx/15 text-athlynx">
              <Lock className="h-5 w-5" strokeWidth={2} />
            </div>
            <DialogHeader className="mt-3 text-left">
              <DialogTitle className="text-2xl tracking-tight text-foreground">{tf("Passez à Athlynx Pro")}</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1" data-testid="paywall-reason">
                {tf(reason)}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <div className="px-6">
          <div className="rounded-xl border border-border bg-secondary/40 p-4 flex items-baseline gap-1" data-testid="paywall-price-row">
            {price ? (
              <>
                <span className="text-3xl font-semibold tracking-tight text-foreground" data-testid="paywall-price">{fmt(price.amount)}</span>
                <span className="text-sm text-muted-foreground">{tf(iv === "annual" ? "/an" : "/mois")}</span>
                <span className="ml-auto text-xs text-muted-foreground/80">{tf("Sans engagement")}</span>
              </>
            ) : (
              <>
                <span className="h-8 w-24 rounded-md bg-secondary animate-pulse" aria-hidden />
                <span className="ml-auto text-xs text-muted-foreground/80">{tf("Sans engagement")}</span>
              </>
            )}
          </div>
        </div>

        <ul className="px-6 py-3 space-y-2" data-testid="paywall-features">
          {(features.length ? features : null)?.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-foreground/90">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" strokeWidth={2} />
              <span>{tf(f)}</span>
            </li>
          ))}
        </ul>

        <div className="px-6 pb-6 pt-1 flex items-center gap-3">
          <Button
            variant="outline"
            onClick={hide}
            className="border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            data-testid="paywall-later-btn"
          >
            {tf("Plus tard")}
          </Button>
          <SubscribeButton
            planId="pro"
            className="ml-auto"
            label={tf("S'abonner")}
            testid="paywall-subscribe-btn"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
